import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Stack,
  Group,
  Text,
  Textarea,
  NumberInput,
  Box,
  Divider,
  Button,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { TRAVEL_STATUSES } from "@/config/travelStatuses";

import { ApiError } from "@/utils/apiResultHandler";
import { kmToMiles } from "@/utils/formatHelpers";
import { stringifyObject } from "@/utils/misc";
import { localStringToUtc, getCurrentLocalDateTimeString } from "@/utils/dateHelpers";

import { Travel, TravelInput } from "@/utils/api/TravelService";
import { Expense, ExpenseInput } from "@/utils/api/ExpenseService";

import { usePlaceLocation } from "@/hooks/usePlaceLocation";

import { useDashboardContext } from "@/contexts/DashboardContext";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useActiveTravelContext } from "@/contexts/ActiveTravelContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { AppIcon } from "@/components/generic/AppIcon";

import { PlaceLocationSection } from "@/components/features/PlaceLocationSection";
import { TripSummaryCard } from "@/components/features/Travels/TripSummaryCard";

// ============================================
// Types
// ============================================

export interface ActiveTravelActionDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Callback when drawer should close */
  onClose: () => void;

  /** The active travel to act on */
  activeTravel: Travel | null;

  /** Callback after a waypoint is successfully added */
  onWaypointAdded?: (waypoint: Expense | null) => void;

  /** Callback after travel is successfully completed */
  onTravelCompleted?: (travel: Travel) => void;
}

interface FormState {
  odometer: number | null;
  distance: number | null; // Manual mode only, used on complete
  notes: string;
  whenDone: string; // Local time string for DateTimePicker
}

// ============================================
// Constants
// ============================================

const KNOWN_FORM_FIELDS = [
  "pointType",
  "odometer",
  "distance",
  "distanceIn",
  "whereDone",
  "location",
  "latitude",
  "longitude",
  "comments",
  "notes",
  "whenDone",
  "endTime",
  "carId",
  "travelId",
  "status",
  "lastRecord",
  "savedPlaceId",
  "savePlace",
];

// ============================================
// Helper Functions
// ============================================

const getInitialFormState = (
  latestWaypointOdometer?: number | null,
  startOdometer?: number | null,
  gpsDistanceKm?: number,
  carMileageIn?: string,
  userDistanceIn?: string,
): FormState => {
  let odometer: number | null = latestWaypointOdometer ?? null;
  let distance: number | null = null;

  const hasOdometer = startOdometer != null;
  const hasGps = gpsDistanceKm != null && gpsDistanceKm > 0;

  if (hasOdometer && hasGps) {
    // Odometer mode: start + GPS distance (converted to car's unit)
    const gpsInCarUnit = carMileageIn === "mi" ? kmToMiles(gpsDistanceKm) : gpsDistanceKm;
    odometer = Math.round(startOdometer + gpsInCarUnit);
  }

  if (!hasOdometer && hasGps) {
    // Manual mode: GPS distance converted to user's unit
    distance = userDistanceIn === "mi"
      ? Math.round(kmToMiles(gpsDistanceKm) * 10) / 10
      : Math.round(gpsDistanceKm * 10) / 10;
  }

  return {
    odometer,
    distance,
    notes: "",
    whenDone: getCurrentLocalDateTimeString(),
  };
};

// ============================================
// Component
// ============================================

const ActiveTravelActionDrawerComponent: React.FC<ActiveTravelActionDrawerProps> = ({
  opened,
  onClose,
  activeTravel,
  onWaypointAdded,
  onTravelCompleted,
}) => {
  const { t } = useTranslation(["travels", "common"]);
  const { distanceLabelShort, distanceUnit } = useUserProfileContext();
  const { getCarById } = useDashboardContext();
  const {
    addWaypoint,
    completeTravel,
    latestWaypoint,
    isTrackingPaused,
    hasTracking,
    trackingSession,
    discardTracking,
    //isPausingTracking,
    isDiscardingTracking,
  } = useActiveTravelContext();
  const { isMobile } = useAppEnvironment();

  // Form state
  const [formState, setFormState] = useState<FormState>(() =>
    getInitialFormState()
  );

  // Place/location state via reusable hook
  const placeLocation = usePlaceLocation({
    enabled: opened,
    autoLocate: true,
    travelType: activeTravel?.travelType,
    includePointType: true,
    onFieldChange: () => setPlaceDirty(true),
  });

  // Operation state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Track which action is in progress for button loading states
  const [submittingAction, setSubmittingAction] = useState<"waypoint" | "complete" | null>(null);

  // ============================================
  // Dirty State Tracking
  // ============================================

  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const [placeDirty, setPlaceDirty] = useState(false);

  useEffect(() => {
    if (!opened) return;
    const formChanged =
      stringifyObject(formState) !== initialSnapshotRef.current;
    setIsDirty(formChanged || placeDirty);
  }, [formState, placeDirty, opened]);

  // ============================================
  // Error Handling
  // ============================================

  const { fields: fieldErrors, genericErrors } = useMemo(() => {
    return ApiError.getFormErrors(lastError, KNOWN_FORM_FIELDS);
  }, [lastError]);

  const formattedGenericError = useMemo(() => {
    return ApiError.formatErrors(genericErrors);
  }, [genericErrors]);

  // ============================================
  // Computed Values
  // ============================================

  const selectedCar = useMemo(() => {
    if (!activeTravel?.carId) return null;
    return getCarById(activeTravel.carId);
  }, [activeTravel?.carId, getCarById]);

  const odometerUnit = useMemo(() => {
    return selectedCar?.mileageIn || distanceLabelShort;
  }, [selectedCar?.mileageIn, distanceLabelShort]);

  // const carMileageIn = useMemo(() => {
  //   return selectedCar?.mileageIn || "km";
  // }, [selectedCar?.mileageIn]);

  // Is the trip in odometer mode? (started with an odometer reading)
  const isOdometerMode = useMemo(() => {
    return (
      (activeTravel?.firstOdometer != null) ||
      (activeTravel?.firstRecord?.odometer != null)
    );
  }, [activeTravel]);

  const startOdometer = useMemo(() => {
    return (
      activeTravel?.firstRecord?.odometer ??
      activeTravel?.firstOdometer ??
      null
    );
  }, [activeTravel]);

  // Distance traveled (based on form's current odometer vs start)
  const traveledDistance = useMemo(() => {
    if (!isOdometerMode || startOdometer === null || formState.odometer === null) {
      return null;
    }
    const distance = formState.odometer - startOdometer;
    return distance > 0 ? distance : null;
  }, [isOdometerMode, startOdometer, formState.odometer]);

  // Odometer validation
  const isOdometerValid = useMemo(() => {
    if (startOdometer === null || formState.odometer === null) {
      return true;
    }
    return formState.odometer >= startOdometer;
  }, [startOdometer, formState.odometer]);

  // Min date for the time picker (trip start)
  const minDate = useMemo(() => {
    if (activeTravel?.firstDttm) {
      return new Date(activeTravel.firstDttm);
    }
    return undefined;
  }, [activeTravel?.firstDttm]);

  // ============================================
  // Reset form when drawer opens
  // ============================================

  useEffect(() => {
    if (opened) {
      const gpsDistanceKm = hasTracking ? trackingSession?.gpsDistance : undefined;

      const currentStartOdometer =
        activeTravel?.firstRecord?.odometer ??
        activeTravel?.firstOdometer ??
        null;

      const newFormState = getInitialFormState(
        latestWaypoint?.odometer,
        currentStartOdometer,
        gpsDistanceKm,
        selectedCar?.mileageIn || "km",
        distanceUnit,
      );

      setFormState(newFormState);
      initialSnapshotRef.current = stringifyObject(newFormState);
      setIsDirty(false);
      setPlaceDirty(false);
      setSubmittingAction(null);

      placeLocation.reset();
      setLastError(null);
    }
  }, [opened, latestWaypoint?.odometer]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Handlers
  // ============================================

  const handleOdometerChange = useCallback((value: string | number) => {
    setFormState((prev) => ({
      ...prev,
      odometer: typeof value === "number" ? value : null,
    }));
  }, []);

  const handleDistanceChange = useCallback((value: string | number) => {
    setFormState((prev) => ({
      ...prev,
      distance: typeof value === "number" ? value : null,
    }));
  }, []);

  const handleNotesChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFormState((prev) => ({ ...prev, notes: event.target.value }));
    },
    []
  );

  const handleWhenDoneChange = useCallback((value: string | null) => {
    setFormState((prev) => ({
      ...prev,
      whenDone: value || getCurrentLocalDateTimeString(),
    }));
  }, []);

  // ============================================
  // Submit: Add Waypoint
  // ============================================

  const handleAddWaypoint = useCallback(async () => {
    if (!activeTravel?.id || !activeTravel?.carId) return;

    setLastError(null);
    setIsSubmitting(true);
    setSubmittingAction("waypoint");

    const placeFields = placeLocation.getPlaceFields();

    const params: ExpenseInput = {
      whenDone: localStringToUtc(formState.whenDone),
      totalPrice: 0,
      odometer: formState.odometer || undefined,
      comments: formState.notes || undefined,
      ...placeFields,
    };

    try {
      const result = await addWaypoint(params);

      if (result.code === OP_RESULT_CODES.OK) {
        setIsDirty(false);
        onClose();
        const createdWaypoint = result.data
          ? (result.data as Expense[])[0]
          : null;
        onWaypointAdded?.(createdWaypoint);
      } else {
        setLastError(ApiError.fromResult(result, "Failed to add waypoint"));
      }
    } catch (error) {
      setLastError(ApiError.fromException(error, "Failed to add waypoint"));
    } finally {
      setIsSubmitting(false);
      setSubmittingAction(null);
    }
  }, [activeTravel, formState, placeLocation, addWaypoint, onClose, onWaypointAdded]);

  // ============================================
  // Submit: Complete Trip
  // ============================================

  const handleCompleteTrip = useCallback(async () => {
    if (!activeTravel?.id || !activeTravel?.carId) return;

    setLastError(null);
    setIsSubmitting(true);
    setSubmittingAction("complete");

    const placeFields = placeLocation.getPlaceFields();

    const params: TravelInput = {
      status: TRAVEL_STATUSES.COMPLETED,
      savePlace: placeFields.savePlace || undefined,
      lastRecord: {
        carId: activeTravel.carId,
        whenDone: localStringToUtc(formState.whenDone),
        odometer: isOdometerMode ? formState.odometer || undefined : undefined,
        comments: formState.notes || undefined,
        pointType: placeFields.pointType || undefined,
        whereDone: placeFields.whereDone || undefined,
        location: placeFields.location || undefined,
        address1: placeFields.address1 || undefined,
        address2: placeFields.address2 || undefined,
        city: placeFields.city || undefined,
        stateProvince: placeFields.stateProvince || undefined,
        postalCode: placeFields.postalCode || undefined,
        countryId: placeFields.countryId || undefined,
        latitude: placeFields.latitude ?? undefined,
        longitude: placeFields.longitude ?? undefined,
        savedPlaceId: placeFields.savedPlaceId || undefined,
      },
    };

    // Manual mode: include total distance
    if (!isOdometerMode && formState.distance) {
      params.distance = formState.distance;
      params.distanceIn = distanceUnit;
    }

    try {
      const result = await completeTravel(activeTravel.id, params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const completedTravel = (result.data as Travel[])[0];
        setIsDirty(false);
        onClose();
        if (completedTravel) {
          onTravelCompleted?.(completedTravel);
        }
      } else {
        setLastError(ApiError.fromResult(result, "Failed to complete travel"));
      }
    } catch (error) {
      setLastError(ApiError.fromException(error, "Failed to complete travel"));
    } finally {
      setIsSubmitting(false);
      setSubmittingAction(null);
    }
  }, [
    activeTravel,
    formState,
    isOdometerMode,
    distanceUnit,
    placeLocation,
    completeTravel,
    onClose,
    onTravelCompleted,
  ]);

  // ============================================
  // Validation
  // ============================================

  const canSubmit = useMemo(() => {
    if (!activeTravel?.id || isSubmitting) return false;
    if (isOdometerMode && !isOdometerValid) return false;
    return true;
  }, [activeTravel?.id, isSubmitting, isOdometerMode, isOdometerValid]);

  // ============================================
  // Render
  // ============================================

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("travels:drawers.activeTravelAction.title")}
      position="right"
      size="lg"
      responsive
      mobileHeightPercent={90}
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      actionType="custom"
      customActions={
        <Group gap="sm" grow>
          <Button
            variant="filled"
            color="red"
            size="lg"
            onClick={handleCompleteTrip}
            disabled={!canSubmit}
            loading={submittingAction === "complete"}
            leftSection={
              submittingAction !== "complete" ? (
                <AppIcon iconName="CircleCheck" size={18} />
              ) : undefined
            }
          >
            {t("travels:drawers.activeTravelAction.actions.completeTrip")}
          </Button>
          <Button
            variant="filled"
            size="lg"
            onClick={handleAddWaypoint}
            disabled={!canSubmit}
            loading={submittingAction === "waypoint"}
            leftSection={
              submittingAction !== "waypoint" ? (
                <AppIcon iconName="MapPin" size={18} />
              ) : undefined
            }
          >
            {t("travels:drawers.activeTravelAction.actions.addWaypoint")}
          </Button>
        </Group>
      }
      isDirty={isDirty}
    >
      <Stack gap="md">
        {/* ── Trip Summary (Medium) ── */}
        <TripSummaryCard
          activeTravel={activeTravel}
        />

        {/* Tracking Controls (when tracking is active or paused) */}
        {hasTracking && (
          <Box
            p="sm"
            style={{
              backgroundColor: isTrackingPaused
                ? "var(--mantine-color-yellow-0)"
                : "var(--mantine-color-blue-0)",
              borderRadius: "var(--mantine-radius-md)",
              border: `1px solid ${isTrackingPaused
                ? "var(--mantine-color-yellow-2)"
                : "var(--mantine-color-blue-2)"
                }`,
            }}
          >
            <Group gap="sm" wrap="nowrap">
              <AppIcon
                iconName={isTrackingPaused ? "PlayerPause" : "MapPin"}
                size={18}
                color={
                  isTrackingPaused
                    ? "var(--mantine-color-yellow-6)"
                    : "var(--mantine-color-blue-6)"
                }
                style={{ flexShrink: 0 }}
              />
              <Box style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  {isTrackingPaused
                    ? t("travels:tracking.statusPaused", {
                      defaultValue: "GPS tracking is paused",
                    })
                    : t("travels:tracking.statusActive", {
                      defaultValue: "GPS tracking is active",
                    })}
                </Text>
                <Text size="xs" c="dimmed" mt={2}>
                  {isTrackingPaused
                    ? t("travels:tracking.resumeHint", {
                      defaultValue: "Resume from the tracking banner when you continue driving",
                    })
                    : t("travels:tracking.activeHint", {
                      defaultValue: "Your route is being recorded automatically",
                    })}
                </Text>
              </Box>
            </Group>

            {isTrackingPaused && (
              <Button
                variant="subtle"
                color="red"
                size="xs"
                mt="xs"
                leftSection={<AppIcon iconName="Trash" size={14} />}
                onClick={async () => {
                  await discardTracking();
                }}
                loading={isDiscardingTracking}
                fullWidth={false}
              >
                {t("travels:tracking.actions.discard", {
                  defaultValue: "Discard tracking data",
                })}
              </Button>
            )}
          </Box>
        )}

        {/* ── Date/Time ── */}
        <DateTimePicker
          label={t("travels:drawers.activeTravelAction.fields.whenDone.label")}
          value={formState.whenDone}
          onChange={handleWhenDoneChange}
          error={fieldErrors.whenDone || fieldErrors.endTime}
          minDate={minDate}
          maxDate={new Date()}
          size="md"
          clearable={false}
          valueFormat="MMM DD, YYYY HH:mm"
          dropdownType={isMobile ? "modal" : "popover"}
          timePickerProps={{
            withDropdown: true,
            popoverProps: { withinPortal: true },
            format: "12h",
          }}
        />

        <Divider
          label={t("travels:drawers.activeTravelAction.sections.location")}
          labelPosition="center"
        />

        {/* ── Place, Address, Map Preview, Save Place Toggle ── */}
        <PlaceLocationSection
          placeLocation={placeLocation}
          showPointType
          fieldErrors={fieldErrors}
          placeNameLabel={t("travels:drawers.activeTravelAction.fields.whereDone.label")}
          placeNamePlaceholder={t("travels:drawers.activeTravelAction.fields.whereDone.placeholder")}
          addressLabel={t("travels:drawers.activeTravelAction.fields.location.label")}
          addressPlaceholder={t("travels:drawers.activeTravelAction.fields.location.placeholder")}
        />

        <Divider
          label={t("travels:drawers.activeTravelAction.sections.odometerDistance")}
          labelPosition="center"
        />

        {/* ── Odometer (always shown — optional field) ── */}
        {isOdometerMode && (
          <Stack gap="xs">
            <NumberInput
              label={t("travels:drawers.activeTravelAction.fields.odometer.label")}
              description={
                startOdometer !== null
                  ? t("travels:drawers.activeTravelAction.fields.odometer.description", {
                    startOdometer: startOdometer.toLocaleString(),
                    unit: t(`common:units.distance.${odometerUnit}Short`),
                  })
                  : undefined
              }
              placeholder={t("travels:drawers.activeTravelAction.fields.odometer.placeholder")}
              value={formState.odometer ?? ""}
              onChange={handleOdometerChange}
              error={
                fieldErrors.odometer ||
                (!isOdometerValid
                  ? t("travels:drawers.activeTravelAction.errors.odometerTooLow", {
                    startOdometer: startOdometer?.toLocaleString(),
                  })
                  : undefined)
              }
              min={0}
              max={9999999}
              rightSection={
                <Text size="sm" c="dimmed" pr="sm">
                  {t(`common:units.distance.${odometerUnit}Short`)}
                </Text>
              }
              rightSectionWidth={50}
              size="md"
              hideControls
            />

            {/* Distance Traveled Display (odometer mode) */}
            {traveledDistance !== null && (
              <Box
                p="sm"
                style={{
                  backgroundColor: "var(--mantine-color-green-0)",
                  borderRadius: "var(--mantine-radius-sm)",
                  border: "1px solid var(--mantine-color-green-2)",
                }}
              >
                <Group gap="sm" justify="center">
                  <AppIcon
                    iconName="Route"
                    size={20}
                    color="var(--mantine-color-green-6)"
                  />
                  <Text size="sm" c="green.7">
                    {t("travels:drawers.activeTravelAction.distanceTraveled")}
                  </Text>
                  <Text size="lg" fw={700} c="green.8">
                    {traveledDistance.toLocaleString()}{" "}
                    {t(`common:units.distance.${odometerUnit}`)}
                  </Text>
                </Group>
              </Box>
            )}
          </Stack>
        )}

        {/* ── Manual Distance (only when NOT in odometer mode) ── */}
        {!isOdometerMode && (
          <NumberInput
            label={t("travels:drawers.activeTravelAction.fields.distance.label")}
            description={t("travels:drawers.activeTravelAction.fields.distance.description")}
            placeholder={t("travels:drawers.activeTravelAction.fields.distance.placeholder")}
            value={formState.distance ?? ""}
            onChange={handleDistanceChange}
            error={fieldErrors.distance}
            min={0}
            max={99999}
            decimalScale={1}
            rightSection={
              <Text size="sm" c="dimmed" pr="sm">
                {t(`common:${distanceLabelShort}`)}
              </Text>
            }
            rightSectionWidth={50}
            size="md"
            hideControls
          />
        )}

        {/* ── Mode Info Hint ── */}
        <Box
          p="sm"
          style={{
            backgroundColor: "var(--mantine-color-gray-0)",
            borderRadius: "var(--mantine-radius-sm)",
          }}
        >
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <AppIcon
              iconName="InfoCircle"
              size={18}
              color="var(--mantine-color-gray-6)"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Text size="sm" c="dimmed">
              {isOdometerMode
                ? t("travels:drawers.activeTravelAction.hints.odometerMode")
                : t("travels:drawers.activeTravelAction.hints.manualMode")}
            </Text>
          </Group>
        </Box>

        {/* ── Notes (Optional — shared for both actions) ── */}
        <Textarea
          label={t("travels:drawers.activeTravelAction.fields.notes.label")}
          placeholder={t("travels:drawers.activeTravelAction.fields.notes.placeholder")}
          value={formState.notes}
          onChange={handleNotesChange}
          error={fieldErrors.notes || fieldErrors.comments}
          minRows={2}
          maxRows={4}
          autosize
          size="md"
        />
      </Stack>
    </AppDrawer>
  );
};

export const ActiveTravelActionDrawer = React.memo(ActiveTravelActionDrawerComponent);